import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Banknote,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  LayoutDashboard,
  LineChart,
  Monitor,
  Network,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  Users,
  Wallet
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { puskudAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAdminPermission } from '../utils/adminPermissions';
import { formatCurrency, formatNumber } from '../utils/operations';
import { formatMonthLabel } from '../utils/i18nFormatters';

const monthOptions = Array.from({ length: 12 }, (_, index) => index + 1);
const statusOptions = ['pending', 'approved', 'completed', 'rejected', 'all'];

const pickText = (...values) => values
  .map((value) => String(value ?? '').trim())
  .find(Boolean) || '';

const getPersonName = (person, fallback = '-') => pickText(
  person?.displayName,
  person?.nickname,
  person?.name,
  person?.phoneNumber,
  person?.phone,
  person?.email,
  fallback
);

const getRegionalPartnerName = (person, fallback) => {
  const name = getPersonName(person, fallback);
  return /^unbound\s+puskud$/i.test(name) ? fallback : name;
};

const getPersonContact = (person, fallback = '-') => pickText(
  person?.phoneNumber,
  person?.phone,
  person?.email,
  fallback
);

const getDeviceLabel = (unit) => pickText(
  unit?.deviceName,
  unit?.deviceId,
  unit?.stationCode,
  `#${unit?.id || unit?.unitId || '-'}`
);

const getUnitId = (unit) => unit?.id || unit?.unitId;
const getRegionalPartnerLabel = (t) => t('puskudManagement.labels.regionalPartner', { defaultValue: '区域合伙人 RP' });
const getPartnerLabel = (t) => t('puskudManagement.labels.partner', { defaultValue: '合伙人 PA' });

const getRequestMessage = (result, fallback) => (
  result?.reason?.response?.data?.message || result?.reason?.message || fallback
);

const parseBankAccount = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

function StatCard({ icon: Icon, label, value, hint, tone = 'cyan' }) {
  const tones = {
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    violet: 'border-violet-200 bg-violet-50 text-violet-700',
    slate: 'border-slate-200 bg-slate-50 text-slate-700'
  };

  return (
    <div className={`rounded-lg border p-5 ${tones[tone] || tones.cyan}`}>
      <div className="flex items-center justify-between gap-3 text-xs font-black uppercase">
        <span>{label}</span>
        <Icon size={18} />
      </div>
      <div className="mt-4 break-words text-2xl font-black text-[var(--admin-text-primary)] sm:text-3xl">{value}</div>
      {hint && <div className="mt-2 text-xs text-[var(--admin-text-muted)]">{hint}</div>}
    </div>
  );
}

function TabButton({ active, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-black transition ${
        active
          ? 'admin-primary-button'
          : 'admin-secondary-button hover:border-cyan-300 hover:text-cyan-700'
      }`}
    >
      <Icon size={16} />
      <span>{label}</span>
    </button>
  );
}

function SectionTitle({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="flex flex-col gap-3 border-b border-[var(--admin-border-subtle)] px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex items-start gap-3">
        <div className="admin-muted-panel mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-cyan-700">
          <Icon size={18} />
        </div>
        <div>
          <h3 className="text-lg font-black text-[var(--admin-text-primary)]">{title}</h3>
          {subtitle && <p className="mt-1 text-xs leading-5 text-[var(--admin-text-muted)]">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function UnitChip({ unit }) {
  const unitId = getUnitId(unit);
  return (
    <Link
      to={unitId ? `/units/${unitId}` : '#'}
      className="admin-muted-panel grid min-h-9 max-w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 rounded-lg px-3 py-1.5 text-xs text-[var(--admin-text-secondary)] transition hover:border-cyan-300 hover:text-cyan-700"
    >
      <span className="min-w-0 truncate font-mono font-bold text-cyan-700">{unit.deviceId || unit.stationCode || String(unitId || '').slice(-6)}</span>
      <span className={String(unit.status).toLowerCase() === 'online' ? 'text-emerald-700' : 'text-[var(--admin-text-muted)]'}>
        {unit.status || '-'}
      </span>
      <span className="col-span-2 min-w-0 truncate">{unit.deviceName || getDeviceLabel(unit)}</span>
    </Link>
  );
}

function WorkflowStep({ number, icon: Icon, title, detail, tone = 'cyan' }) {
  const tones = {
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700'
  };

  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.cyan}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="admin-muted-panel flex h-8 w-8 items-center justify-center rounded-full text-sm font-black">{number}</div>
          <Icon size={18} />
        </div>
        <CheckCircle2 size={18} />
      </div>
      <div className="mt-4 font-black text-[var(--admin-text-primary)]">{title}</div>
      <p className="mt-2 text-sm leading-6 text-[var(--admin-text-secondary)]">{detail}</p>
    </div>
  );
}

export default function PUSKUDManagement() {
  const { t, i18n } = useTranslation();
  const currentAdmin = useAuthStore((state) => state.user);
  const canViewPartnerNetwork = hasAdminPermission(currentAdmin, 'view_partner_network');
  const canViewFinance = hasAdminPermission(currentAdmin, 'view_finance');
  const now = new Date();
  const [groups, setGroups] = useState([]);
  const [summary, setSummary] = useState({});
  const [meta, setMeta] = useState({});
  const [finance, setFinance] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalStats, setWithdrawalStats] = useState([]);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [activeTab, setActiveTab] = useState(canViewPartnerNetwork ? 'overview' : 'hierarchy');
  const [payoutStatus, setPayoutStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [financeError, setFinanceError] = useState('');
  const [payoutError, setPayoutError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const hasLoadedRef = useRef(false);

  const loadData = useCallback(async ({ preserveContent = false } = {}) => {
    const showBlockingLoading = !preserveContent && !hasLoadedRef.current;
    setLoading(showBlockingLoading);
    setRefreshing(!showBlockingLoading);
    setError('');
    setFinanceError('');
    setPayoutError('');

    const withdrawalParams = payoutStatus === 'all' ? { limit: 50 } : { status: payoutStatus, limit: 50 };

    try {
      const [hierarchyRes, financeRes, payoutsRes] = await Promise.allSettled([
        puskudAPI.getHierarchy(),
        canViewPartnerNetwork ? puskudAPI.getOperatingSummary({ year, month }) : Promise.resolve(null),
        canViewPartnerNetwork ? puskudAPI.getPayoutOverview(withdrawalParams) : Promise.resolve(null)
      ]);

      if (hierarchyRes.status === 'fulfilled') {
        setGroups(hierarchyRes.value.data?.data || []);
        setSummary(hierarchyRes.value.data?.summary || {});
        setMeta(hierarchyRes.value.data?.meta || {});
      } else {
        setError(getRequestMessage(hierarchyRes, t('puskudManagement.alerts.loadFailed')));
      }

      if (!canViewPartnerNetwork) {
        setFinance(null);
      } else if (financeRes.status === 'fulfilled') {
        setFinance(financeRes.value.data?.data || null);
      } else {
        setFinanceError(getRequestMessage(financeRes, t('puskudManagement.finance.loadFailed', { defaultValue: 'Failed to load Regional Partner RP finance summary' })));
      }

      if (!canViewPartnerNetwork) {
        setWithdrawals([]);
      } else if (payoutsRes.status === 'fulfilled') {
        setWithdrawals(payoutsRes.value.data?.data?.withdrawals || []);
      } else {
        setPayoutError(getRequestMessage(payoutsRes, t('puskudManagement.payouts.loadFailed', { defaultValue: 'Failed to load Regional Partner RP payout requests' })));
      }

      if (!canViewPartnerNetwork) {
        setWithdrawalStats([]);
      } else if (payoutsRes.status === 'fulfilled') {
        setWithdrawalStats(payoutsRes.value.data?.data?.stats || []);
      }
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [canViewPartnerNetwork, month, payoutStatus, t, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredGroups = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return groups;

    const matches = (...values) => values.some((value) => String(value ?? '').trim().toLowerCase().includes(query));
    const countUnits = (kuds) => kuds.reduce(
      (sum, kud) => sum + (kud.stewards || []).reduce((stewardSum, steward) => stewardSum + (steward.units || []).length, 0),
      0
    );
    const countStewards = (kuds) => kuds.reduce((sum, kud) => sum + (kud.stewards || []).filter((steward) => steward.id).length, 0);

    return groups.map((puskud) => {
      const puskudMatches = matches(puskud.id, puskud.name, puskud.nickname, puskud.phoneNumber, puskud.phone, puskud.email);
      if (puskudMatches) return puskud;

      const kuds = (puskud.kuds || []).map((kud) => {
        const kudMatches = matches(kud.id, kud.name, kud.nickname, kud.phoneNumber, kud.phone, kud.email);
        if (kudMatches) return kud;

        const stewards = (kud.stewards || []).map((steward) => {
          const stewardMatches = matches(steward.id, steward.name, steward.nickname, steward.phoneNumber, steward.phone, steward.email);
          if (stewardMatches) return steward;

          const units = (steward.units || []).filter((unit) => matches(
            unit.deviceId,
            unit.deviceName,
            unit.stationCode,
            unit.location,
            unit.status
          ));

          return units.length ? { ...steward, units, unitCount: units.length } : null;
        }).filter(Boolean);

        return stewards.length
          ? {
            ...kud,
            stewards,
            stewardCount: stewards.filter((steward) => steward.id).length,
            unitCount: countUnits([{ ...kud, stewards }])
          }
          : null;
      }).filter(Boolean);

      return kuds.length
        ? {
          ...puskud,
          kuds,
          kudCount: kuds.filter((kud) => kud.id).length,
          stewardCount: countStewards(kuds),
          unitCount: countUnits(kuds)
        }
        : null;
    }).filter(Boolean);
  }, [groups, searchTerm]);

  const financeSummary = finance?.summary || {};
  const puskudFinanceRows = finance?.roles || [];
  const financeDeviceRows = finance?.devices || [];
  const withdrawalTotals = useMemo(
    () => Object.fromEntries((withdrawalStats || []).map((item) => [item.status, Number(item.totalAmount || 0)])),
    [withdrawalStats]
  );

  const tabs = [
    { key: 'overview', icon: LayoutDashboard, label: t('puskudManagement.tabs.overview', { defaultValue: 'Overview' }) },
    { key: 'hierarchy', icon: Network, label: t('puskudManagement.tabs.hierarchy', { defaultValue: 'Device hierarchy' }) },
    { key: 'finance', icon: LineChart, label: t('puskudManagement.tabs.finance', { defaultValue: 'Finance' }) },
    { key: 'payouts', icon: Wallet, label: t('puskudManagement.tabs.payouts', { defaultValue: 'Payouts' }) }
  ].filter((tab) => canViewPartnerNetwork || tab.key === 'hierarchy');

  const periodLabel = `${year}-${String(month).padStart(2, '0')}`;

  const renderHierarchy = () => (
    <section className="admin-card rounded-lg">
      <SectionTitle
        icon={Network}
        title={t('puskudManagement.hierarchy.title')}
        subtitle={t('puskudManagement.hierarchy.subtitle')}
        action={(
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--admin-text-muted)]" size={16} />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t('puskudManagement.searchPlaceholder')}
                className="admin-input w-full rounded-lg py-3 pl-11 pr-4 text-sm outline-none sm:w-80"
              />
            </div>
            <div className="admin-muted-panel rounded-full px-3 py-1 text-xs font-bold text-[var(--admin-text-secondary)]">
              {meta.scope === 'self' ? t('puskudManagement.hierarchy.selfScope') : t('puskudManagement.hierarchy.allScope')}
            </div>
          </div>
        )}
      />

      <div className="space-y-4 p-5">
        {loading && (
          <div className="admin-muted-panel rounded-lg p-12 text-center text-[var(--admin-text-muted)]">
            {t('puskudManagement.loading')}
          </div>
        )}

        {!loading && filteredGroups.length === 0 && (
          <div className="admin-muted-panel rounded-lg p-12 text-center text-[var(--admin-text-muted)]">
            {searchTerm ? t('puskudManagement.noMatch') : t('puskudManagement.empty')}
          </div>
        )}

        {!loading && filteredGroups.map((puskud) => (
          <article key={puskud.id || 'unassigned'} className="admin-muted-panel rounded-lg p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-emerald-100 text-lg font-black text-emerald-700">
                    {getRegionalPartnerName(puskud, 'RP').slice(0, 1).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-xs font-black uppercase tracking-wide text-emerald-700">{getRegionalPartnerLabel(t)}</div>
                    <h4 className="break-words text-xl font-black text-[var(--admin-text-primary)]">
                      {getRegionalPartnerName(puskud, t('puskudManagement.labels.unboundPuskud'))}
                    </h4>
                    <p className="text-sm text-[var(--admin-text-muted)]">
                      {getPersonContact(puskud, t('puskudManagement.labels.noContact'))}
                    </p>
                  </div>
                  {puskud.isUnassigned && (
                    <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-bold text-rose-700">
                      {t('puskudManagement.labels.needsBinding')}
                    </span>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold">
                  <span className="admin-muted-panel rounded-full px-3 py-1">
                    {getPartnerLabel(t)}: {formatNumber(puskud.kudCount || 0)}
                  </span>
                  <span className="admin-muted-panel rounded-full px-3 py-1">
                    {t('puskudManagement.labels.stewards')}: {formatNumber(puskud.stewardCount || 0)}
                  </span>
                  <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
                    {t('puskudManagement.labels.activeUnits')}: {formatNumber(puskud.activeUnitCount || 0)}
                  </span>
                </div>
              </div>

              <div className="grid min-w-[220px] grid-cols-2 gap-3 text-right">
                <div className="admin-card rounded-lg p-3">
                  <div className="text-xs font-black uppercase text-[var(--admin-text-muted)]">{t('puskudManagement.labels.units')}</div>
                  <div className="mt-1 text-2xl font-black text-[var(--admin-text-primary)]">{formatNumber(puskud.unitCount || 0)}</div>
                </div>
                <div className="admin-card rounded-lg p-3">
                  <div className="text-xs font-black uppercase text-[var(--admin-text-muted)]">{t('puskudManagement.labels.onlineUnits')}</div>
                  <div className="mt-1 text-2xl font-black text-emerald-700">{formatNumber(puskud.onlineUnitCount || 0)}</div>
                </div>
              </div>
            </div>

            <div className="mt-5 space-y-4 border-l-2 border-emerald-200 pl-5">
              {(puskud.kuds || []).map((kud) => (
                <div key={kud.id || `${puskud.id || 'unassigned'}-no-kud`} className="relative">
                  <span className="absolute -left-5 top-6 h-0.5 w-5 bg-emerald-200" aria-hidden="true" />
                  <div className="admin-card rounded-lg p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-3">
                      <Briefcase size={18} className="text-cyan-700" />
                      <div>
                        <div className="text-xs font-black uppercase tracking-wide text-cyan-700">{getPartnerLabel(t)}</div>
                        {kud.id ? (
                          <Link to={`/pa/${kud.id}`} className="font-black text-cyan-700 hover:text-cyan-900 hover:underline">
                            {getPersonName(kud, `${getPartnerLabel(t)} #${kud.id}`)}
                          </Link>
                        ) : (
                          <div className="font-black text-[var(--admin-text-secondary)]">{t('puskudManagement.labels.noKud')}</div>
                        )}
                        <div className="text-xs text-[var(--admin-text-muted)]">
                          {getPersonContact(kud, t('puskudManagement.labels.noContact'))}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs font-bold">
                      <span className="admin-muted-panel rounded-full px-3 py-1">
                        {t('puskudManagement.labels.stewards')}: {formatNumber(kud.stewardCount || 0)}
                      </span>
                      <span className="admin-muted-panel rounded-full px-3 py-1">
                        {t('puskudManagement.labels.units')}: {formatNumber(kud.unitCount || 0)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-3 border-l-2 border-cyan-200 pl-5">
                    {(kud.stewards || []).map((steward) => (
                      <div key={steward.id || `${kud.id || 'no-kud'}-no-steward`} className="relative">
                        <span className="absolute -left-5 top-5 h-0.5 w-5 bg-cyan-200" aria-hidden="true" />
                        <div className="admin-muted-panel rounded-lg p-3">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="flex items-center gap-3">
                            <UserCheck size={17} className="text-emerald-700" />
                            <div>
                              {steward.id ? (
                                <Link to={`/stewards/${steward.id}`} className="font-bold text-emerald-700 hover:text-emerald-900 hover:underline">
                                  {getPersonName(steward, `Steward #${steward.id}`)}
                                </Link>
                              ) : (
                                <div className="font-bold text-[var(--admin-text-secondary)]">{t('puskudManagement.labels.noSteward')}</div>
                              )}
                              <div className="text-xs text-[var(--admin-text-muted)]">
                                {getPersonContact(steward, t('puskudManagement.labels.noContact'))} / {formatNumber(steward.unitCount || 0)} {t('puskudManagement.labels.unitSuffix')}
                              </div>
                            </div>
                          </div>

                          <div className="flex max-w-3xl flex-wrap gap-2">
                            {(steward.units || []).map((unit) => <UnitChip key={getUnitId(unit) || unit.deviceId || getDeviceLabel(unit)} unit={unit} />)}
                            {(!steward.units || steward.units.length === 0) && (
                              <span className="text-xs text-[var(--admin-text-muted)]">{t('puskudManagement.labels.noDevices')}</span>
                            )}
                          </div>
                        </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );

  const renderFinanceTable = () => (
    <section className="admin-card rounded-lg">
      <SectionTitle
        icon={LineChart}
        title={t('puskudManagement.finance.title', { defaultValue: 'Regional Partner RP finance settlement' })}
        subtitle={t('puskudManagement.finance.subtitle', { defaultValue: 'Revenue, volume, payable ledger, and payout status are grouped by device-level Regional Partner RP binding.' })}
      />
      <div className="space-y-5 p-5">
        {financeError && <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">{financeError}</div>}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={LineChart} label={t('puskudManagement.finance.totalRevenue', { defaultValue: 'Monthly revenue' })} value={formatCurrency(financeSummary.totalRevenue || 0)} tone="cyan" />
          <StatCard icon={Banknote} label={t('puskudManagement.finance.payable', { defaultValue: 'Regional Partner RP payable' })} value={formatCurrency(financeSummary.totalProfit || 0)} tone="emerald" />
          <StatCard icon={Wallet} label={t('puskudManagement.finance.pendingPayout', { defaultValue: 'Pending payout' })} value={formatCurrency(financeSummary.pendingWithdrawal || 0)} tone="amber" />
          <StatCard icon={Monitor} label={t('puskudManagement.finance.totalVolume', { defaultValue: 'Monthly volume' })} value={`${formatNumber(financeSummary.totalVolume || 0)} L`} tone="violet" />
        </div>

        <div className="overflow-x-auto rounded-lg border border-[var(--admin-border-subtle)]">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="bg-[var(--admin-surface-soft)] text-xs font-black uppercase text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{getRegionalPartnerLabel(t)}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.devices', { defaultValue: 'Devices' })}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.revenue', { defaultValue: 'Revenue' })}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.volume', { defaultValue: 'Volume' })}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.payable', { defaultValue: 'Payable' })}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.pendingPayout', { defaultValue: 'Pending payout' })}</th>
              </tr>
            </thead>
            <tbody className="text-[var(--admin-text-secondary)]">
              {loading ? (
                <tr><td colSpan="6" className="p-10 text-center text-[var(--admin-text-muted)]">{t('puskudManagement.loading')}</td></tr>
              ) : puskudFinanceRows.length === 0 ? (
                <tr><td colSpan="6" className="p-10 text-center text-[var(--admin-text-muted)]">{t('puskudManagement.finance.empty', { defaultValue: 'No Regional Partner RP finance rows for this period.' })}</td></tr>
              ) : puskudFinanceRows.map((row) => (
                <tr key={row.userId || row.userName} className="border-t border-[var(--admin-border-subtle)] hover:bg-[var(--admin-surface-soft)]">
                  <td className="p-4">
                    <div className="font-black text-[var(--admin-text-primary)]">
                      {getPersonName({ name: row.userName, phoneNumber: row.phoneNumber, email: row.email }, `${getRegionalPartnerLabel(t)} #${row.userId}`)}
                    </div>
                    <div className="mt-1 text-xs text-[var(--admin-text-muted)]">
                      {getPersonContact(row, t('puskudManagement.labels.noContact'))}
                    </div>
                  </td>
                  <td className="p-4 text-right font-mono">{formatNumber(row.deviceCount || 0)}</td>
                  <td className="p-4 text-right font-mono text-cyan-700">{formatCurrency(row.totalRevenue || 0)}</td>
                  <td className="p-4 text-right font-mono">{formatNumber(row.totalVolume || 0)} L</td>
                  <td className="p-4 text-right font-mono text-emerald-700">{formatCurrency(row.totalProfit || 0)}</td>
                  <td className="p-4 text-right font-mono text-amber-700">{formatCurrency(row.pendingWithdrawal || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="overflow-x-auto rounded-lg border border-[var(--admin-border-subtle)]">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="bg-[var(--admin-surface-soft)] text-xs font-black uppercase text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{t('puskudManagement.finance.deviceTable.device', { defaultValue: 'Device' })}</th>
                <th className="p-4">{getRegionalPartnerLabel(t)}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.revenue', { defaultValue: 'Revenue' })}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.volume', { defaultValue: 'Volume' })}</th>
                <th className="p-4 text-right">{t('puskudManagement.finance.table.payable', { defaultValue: 'Payable' })}</th>
                <th className="p-4 text-center">{t('puskudManagement.finance.deviceTable.status', { defaultValue: 'Status' })}</th>
              </tr>
            </thead>
            <tbody className="text-[var(--admin-text-secondary)]">
              {financeDeviceRows.length === 0 ? (
                <tr><td colSpan="6" className="p-10 text-center text-[var(--admin-text-muted)]">{t('puskudManagement.finance.noDeviceRows', { defaultValue: 'No device finance rows yet.' })}</td></tr>
              ) : financeDeviceRows.map((unit) => (
                <tr key={unit.unitId || unit.deviceId} className="border-t border-[var(--admin-border-subtle)] hover:bg-[var(--admin-surface-soft)]">
                  <td className="p-4">
                    <Link to={`/units/${unit.unitId}`} className="font-mono font-black text-cyan-700 hover:text-cyan-900 hover:underline">
                      {unit.deviceId || unit.unitId}
                    </Link>
                    <div className="mt-1 max-w-[240px] truncate text-xs text-[var(--admin-text-muted)]">{getDeviceLabel(unit)}</div>
                  </td>
                  <td className="p-4">{unit.puskudName || '-'}</td>
                  <td className="p-4 text-right font-mono">{formatCurrency(unit.totalRevenue || 0)}</td>
                  <td className="p-4 text-right font-mono">{formatNumber(unit.totalVolume || 0)} L</td>
                  <td className="p-4 text-right font-mono text-emerald-700">{formatCurrency(unit.totalProfit || 0)}</td>
                  <td className="p-4 text-center">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${String(unit.status).toLowerCase() === 'online' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
                      {unit.status || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );

  const renderPayouts = () => (
    <section className="admin-card rounded-lg">
      <SectionTitle
        icon={Wallet}
        title={t('puskudManagement.payouts.title', { defaultValue: 'Electronic wallet payout requests' })}
        subtitle={t('puskudManagement.payouts.subtitle', { defaultValue: 'Regional Partner RP withdrawal requests are reviewed without mixing Partner PA or steward accounts.' })}
        action={(
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setPayoutStatus(status)}
                className={`rounded-lg px-3 py-2 text-xs font-black uppercase transition ${
                  payoutStatus === status ? 'admin-primary-button' : 'admin-secondary-button'
                }`}
              >
                {t(`puskudManagement.payouts.status.${status}`, { defaultValue: status })}
              </button>
            ))}
          </div>
        )}
      />

      <div className="space-y-5 p-5">
        {payoutError && <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">{payoutError}</div>}

        <div className="grid gap-4 md:grid-cols-4">
          <StatCard icon={ClipboardList} label={t('puskudManagement.payouts.pending', { defaultValue: 'Pending' })} value={formatCurrency(withdrawalTotals.pending || financeSummary.pendingWithdrawal || 0)} tone="amber" />
          <StatCard icon={CheckCircle2} label={t('puskudManagement.payouts.approved', { defaultValue: 'Approved' })} value={formatCurrency(withdrawalTotals.approved || financeSummary.approvedWithdrawal || 0)} tone="emerald" />
          <StatCard icon={Banknote} label={t('puskudManagement.payouts.completed', { defaultValue: 'Completed' })} value={formatCurrency(withdrawalTotals.completed || financeSummary.completedWithdrawal || 0)} tone="cyan" />
          <StatCard icon={AlertTriangle} label={t('puskudManagement.payouts.rejected', { defaultValue: 'Rejected' })} value={formatCurrency(withdrawalTotals.rejected || financeSummary.rejectedWithdrawal || 0)} tone="rose" />
        </div>

        <div className="overflow-x-auto rounded-lg border border-[var(--admin-border-subtle)]">
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead className="bg-[var(--admin-surface-soft)] text-xs font-black uppercase text-[var(--admin-text-muted)]">
              <tr>
                <th className="p-4">{t('puskudManagement.payouts.table.applicant', { defaultValue: 'Applicant' })}</th>
                <th className="p-4 text-right">{t('puskudManagement.payouts.table.amount', { defaultValue: 'Amount' })}</th>
                <th className="p-4">{t('puskudManagement.payouts.table.bank', { defaultValue: 'Bank account' })}</th>
                <th className="p-4">{t('puskudManagement.payouts.table.requestDate', { defaultValue: 'Request date' })}</th>
                <th className="p-4 text-center">{t('puskudManagement.payouts.table.status', { defaultValue: 'Status' })}</th>
              </tr>
            </thead>
            <tbody className="text-[var(--admin-text-secondary)]">
              {loading ? (
                <tr><td colSpan="5" className="p-10 text-center text-[var(--admin-text-muted)]">{t('puskudManagement.loading')}</td></tr>
              ) : withdrawals.length === 0 ? (
                <tr><td colSpan="5" className="p-10 text-center text-[var(--admin-text-muted)]">{t('puskudManagement.payouts.empty', { defaultValue: 'No Regional Partner RP payout requests match this status.' })}</td></tr>
              ) : withdrawals.map((item) => {
                const bankAccount = parseBankAccount(item.bankAccount);
                const applicantName = getPersonName(item.applicant, `User #${item.userId}`);
                return (
                  <tr key={item.id} className="border-t border-[var(--admin-border-subtle)] hover:bg-[var(--admin-surface-soft)]">
                    <td className="p-4">
                      <div className="font-black text-[var(--admin-text-primary)]">{applicantName}</div>
                      <div className="mt-1 text-xs text-[var(--admin-text-muted)]">
                        {getRegionalPartnerLabel(t)} / {getPersonContact(item.applicant, '-')}
                      </div>
                    </td>
                    <td className="p-4 text-right font-mono text-cyan-700">{formatCurrency(item.amount || 0)}</td>
                    <td className="p-4">
                      {bankAccount ? (
                        <div>
                          <div className="font-semibold text-[var(--admin-text-primary)]">{bankAccount.bankName || '-'}</div>
                          <div className="mt-1 text-xs text-[var(--admin-text-muted)]">{bankAccount.accountNumber || '-'} / {bankAccount.accountHolder || bankAccount.accountName || '-'}</div>
                        </div>
                      ) : (
                        <span className="text-[var(--admin-text-muted)]">{t('puskudManagement.payouts.noBank', { defaultValue: 'No bank details' })}</span>
                      )}
                    </td>
                    <td className="p-4">{item.requestDate ? new Date(item.requestDate).toLocaleString() : '-'}</td>
                    <td className="p-4 text-center">
                      <span className="admin-muted-panel rounded-full px-3 py-1 text-xs font-bold uppercase">
                        {item.status || '-'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );

  return (
    <div className="admin-text-primary space-y-6 p-4 sm:p-6 xl:p-8">
      <header className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h2 className="flex items-center gap-3 text-3xl font-black">
            <ShieldCheck className="text-emerald-700" size={30} />
            {t('puskudManagement.title')}
          </h2>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          {canViewPartnerNetwork && (
            <div className="admin-muted-panel flex flex-wrap gap-2 rounded-lg p-2">
              <CalendarDays size={18} className="mt-2 text-[var(--admin-text-muted)]" />
              <select
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                className="admin-input rounded-md px-3 py-2 text-sm outline-none"
              >
                {[2025, 2026, 2027].map((item) => <option key={item} value={item}>{item}</option>)}
              </select>
              <select
                value={month}
                onChange={(event) => setMonth(Number(event.target.value))}
                className="admin-input rounded-md px-3 py-2 text-sm outline-none"
              >
                {monthOptions.map((item) => <option key={item} value={item}>{formatMonthLabel(item, i18n.language)}</option>)}
              </select>
            </div>
          )}
          {canViewFinance && (
            <Link
              to="/profit-sharing-finance"
              className="admin-secondary-button inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-black transition hover:border-cyan-300 hover:text-cyan-700"
            >
              <LineChart size={16} />
              {t('puskudManagement.actions.viewFinance', { defaultValue: 'Finance page' })}
            </Link>
          )}
          <button
            type="button"
            onClick={() => loadData({ preserveContent: true })}
            disabled={loading || refreshing}
            title={t('puskudManagement.actions.refresh')}
            className="admin-primary-button inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading || refreshing ? 'animate-spin' : ''} />
            {t('puskudManagement.actions.refresh')}
          </button>
        </div>
      </header>

      {error && <div className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">{error}</div>}

      <nav className="flex flex-wrap gap-2">
        {tabs.map((tab) => (
          <TabButton
            key={tab.key}
            active={activeTab === tab.key}
            icon={tab.icon}
            label={tab.label}
            onClick={() => setActiveTab(tab.key)}
          />
        ))}
      </nav>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <StatCard icon={ShieldCheck} label={t('puskudManagement.stats.accounts')} value={formatNumber(summary.puskudAccounts || financeSummary.accounts || 0)} />
        <StatCard icon={Briefcase} label={t('puskudManagement.stats.kuds')} value={formatNumber(summary.kuds || 0)} tone="violet" />
        <StatCard icon={Users} label={t('puskudManagement.stats.stewards')} value={formatNumber(summary.stewards || 0)} tone="emerald" />
        <StatCard icon={Monitor} label={t('puskudManagement.stats.units')} value={formatNumber(summary.units || financeSummary.devices || 0)} tone="amber" />
        <StatCard icon={AlertTriangle} label={t('puskudManagement.stats.unboundUnits')} value={formatNumber(summary.unboundUnits || 0)} tone="rose" />
      </section>

      {canViewPartnerNetwork && activeTab === 'overview' && (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
          <section className="admin-card rounded-lg">
            <SectionTitle
              icon={LayoutDashboard}
              title={t('puskudManagement.overview.title', { defaultValue: 'Regional Partner RP operating overview' })}
              subtitle={t('puskudManagement.overview.subtitle', { defaultValue: 'Open work for device binding, monthly settlement, and payout review.' })}
            />
            <div className="grid gap-4 p-5 md:grid-cols-2">
              <StatCard icon={LineChart} label={t('puskudManagement.finance.totalRevenue', { defaultValue: 'Monthly revenue' })} value={formatCurrency(financeSummary.totalRevenue || 0)} hint={periodLabel} tone="cyan" />
              <StatCard icon={Banknote} label={t('puskudManagement.finance.payable', { defaultValue: 'Regional Partner RP payable' })} value={formatCurrency(financeSummary.totalProfit || 0)} hint={t('puskudManagement.finance.ledgerHint', { defaultValue: 'From Regional Partner RP ledger entries' })} tone="emerald" />
              <StatCard icon={Wallet} label={t('puskudManagement.finance.pendingPayout', { defaultValue: 'Pending payout' })} value={formatCurrency(financeSummary.pendingWithdrawal || withdrawalTotals.pending || 0)} tone="amber" />
              <StatCard icon={CreditCard} label={t('puskudManagement.payouts.completed', { defaultValue: 'Completed' })} value={formatCurrency(financeSummary.completedWithdrawal || withdrawalTotals.completed || 0)} tone="slate" />
            </div>
          </section>

          <section className="admin-card rounded-lg">
            <SectionTitle
              icon={ClipboardList}
              title={t('puskudManagement.workflow.title', { defaultValue: 'Settlement workflow' })}
              subtitle={t('puskudManagement.workflow.subtitle', { defaultValue: 'The page now follows the real business order.' })}
            />
            <div className="space-y-3 p-5">
              <WorkflowStep
                number="1"
                icon={Network}
                title={t('puskudManagement.workflow.deviceTitle', { defaultValue: 'Device binding' })}
                detail={t('puskudManagement.workflow.deviceDetail', { defaultValue: 'Regional Partner RP ownership comes from each station device.' })}
                tone={summary.unboundUnits > 0 ? 'amber' : 'emerald'}
              />
              <WorkflowStep
                number="2"
                icon={LineChart}
                title={t('puskudManagement.workflow.financeTitle', { defaultValue: 'Monthly settlement' })}
                detail={t('puskudManagement.workflow.financeDetail', { defaultValue: 'Revenue and volume are grouped by the selected month.' })}
              />
              <WorkflowStep
                number="3"
                icon={Wallet}
                title={t('puskudManagement.workflow.payoutTitle', { defaultValue: 'Wallet payout' })}
                detail={t('puskudManagement.workflow.payoutDetail', { defaultValue: 'Regional Partner RP payout requests stay separated from Partner PA and steward accounts.' })}
                tone={financeSummary.pendingWithdrawal > 0 ? 'amber' : 'cyan'}
              />
            </div>
          </section>
        </div>
      )}

      {activeTab === 'hierarchy' && renderHierarchy()}
      {canViewPartnerNetwork && activeTab === 'finance' && renderFinanceTable()}
      {canViewPartnerNetwork && activeTab === 'payouts' && renderPayouts()}
    </div>
  );
}
